import { useDispatch } from "react-redux";
import { useCallback, useMemo } from "react";

type EffectActionsType = { [key: string]: string };

type InferServiceActions<T extends EffectActionsType> = {
  [K in keyof T]: (...args: any[]) => void;
};

export const useActions = <
  TEffectActions extends EffectActionsType
>(
  effectActions: TEffectActions
): InferServiceActions<TEffectActions> => {
  const dispatch = useDispatch();

  const createActionDispatcher = useCallback(
    (actionType: string) => {
      return (...args: any[]) => {
        const payload = args.length === 0 ? undefined : args.length === 1 ? args[0] : args;
        dispatch({ type: actionType, payload });
      };
    },
    [dispatch]
  );

  type ActionMethodType = InferServiceActions<TEffectActions>[keyof InferServiceActions<TEffectActions>];
  
  const actions = useMemo(() => {
    return Object.entries(effectActions).reduce((acc, [key, actionType]) => {
      const methodName = actionType.split('_')[1];
      acc[methodName as keyof InferServiceActions<TEffectActions>] = createActionDispatcher(actionType) as ActionMethodType;
      return acc;
    }, {} as InferServiceActions<TEffectActions>);
  }, [effectActions, createActionDispatcher]);

  return actions;
};

export default useActions;